import React from 'react';
import { useParams } from 'react-router-dom';
import { repairOrdersService } from '../../services/repairOrdersService';
import { businessService, Business } from '../../services/businessService';
import StatusTimeline from '../../components/ui/StatusTimeline';
import { generateWhatsAppShareUrl } from '../../utils/shareUtils';

const PublicRepairOrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = React.useState<any>(null);
  const [business, setBusiness] = React.useState<Business | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;

      try {
        setLoading(true);
        // Use the public access method for fetching repair order data
        const { order: orderData, error: fetchError } = await repairOrdersService.getRepairOrderByIdPublic(id);

        if (fetchError) {
          setError(fetchError.message);
        } else {
          setOrder(orderData);

          // Fetch business information
          if (orderData) {
            const { business: businessData, error: businessError } = await businessService.getBusinessByRepairOrderId(id);
            if (businessError) {
              console.warn('Failed to load business information:', businessError);
            } else {
              setBusiness(businessData);
            }
          }
        }
      } catch (err) {
        setError('Failed to load repair order');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg max-w-md mx-auto">
          {error || 'Repair order not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
            Repair Order {order.repair_order_number}
          </h1>
          <p className="text-gray-600 text-center mt-2 text-sm sm:text-base">
            Created on {new Date(order.created_at).toLocaleDateString()}
          </p>
        </div>

        <div className="space-y-6">
          {/* Status Timeline */}
          <StatusTimeline currentStatus={order.status} />

          {/* Repair Order Details Container */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Repair Order Details</h3>
            </div>
            <div className="px-4 py-5 sm:p-6">
              {/* First Row - 3 columns */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Repair Order Number</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-medium">{order.repair_order_number}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Entry Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(order.entry_date).toLocaleDateString()}
                  </dd>
                </div>
                {order.completed_date && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Completed Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(order.completed_date).toLocaleDateString()}
                    </dd>
                  </div>
                )}
              </div>

              {/* Second Row - 3 columns */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Customer</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {order.customer?.name || 'Unknown Customer'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Technician</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {order.technician?.name || 'Unassigned'}
                  </dd>
                </div>
              </div>
            </div>
          </div>

          {/* Device Information Container */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Device Information</h3>
            </div>
            <div className="px-4 py-5 sm:p-6">
              {/* First Row - 3 columns */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 mb-6">
                <div>
                  <dt className="text-sm font-medium text-gray-700">Device Brand</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.device_brand}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-700">Device Model</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.device_model}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-700">Device IMEI</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.device_serial}</dd>
                </div>
              </div>

              {/* Second Row - 2 columns */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-700">Problem</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.device_problem}</dd>
                </div>
                {order.device_diagnosis && (
                  <div>
                    <dt className="text-sm font-medium text-gray-700">Diagnosis</dt>
                    <dd className="mt-1 text-sm text-gray-900">{order.device_diagnosis}</dd>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Business Information Container */}
          {business && (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Business Information</h3>
              </div>
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-start space-x-4">
                  {/* Business Logo */}
                  <div className="flex-shrink-0">
                    {business.logo_url ? (
                      <img
                        src={business.logo_url}
                        alt={`${business.name} logo`}
                        className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Business Details */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-medium text-gray-900">{business.name}</h4>

                    <div className="mt-2 space-y-1">
                      {business.phone && (
                        <div className="flex items-center text-sm text-gray-600">
                          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span>{business.phone}</span>
                        </div>
                      )}

                      {business.email && (
                        <div className="flex items-center text-sm text-gray-600">
                          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>{business.email}</span>
                        </div>
                      )}

                      {business.address && (
                        <div className="flex items-start text-sm text-gray-600">
                          <svg className="w-4 h-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{business.address}</span>
                        </div>
                      )}
                    </div>

                    {/* WhatsApp Button */}
                    {business.phone && (
                      <div className="mt-4">
                        <button
                          onClick={() => {
                            const whatsappUrl = generateWhatsAppShareUrl(
                              order.repair_order_number,
                              window.location.href,
                              business.phone
                            );
                            window.open(whatsappUrl, '_blank');
                          }}
                          className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                          </svg>
                          Contact via WhatsApp
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center text-gray-500 text-sm px-4">
            <p className="max-w-2xl mx-auto">This is a public view of your repair order. For any questions, please contact us.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicRepairOrderDetails;